import type { GatewayBrowserClient } from "../gateway.ts";
import type { ChatAttachment } from "../ui-types.ts";
import { extractText } from "../chat/message-extract.ts";
import { generateUUID } from "../uuid.ts";

export type ChatCommandEntry = {
  name: string;
  description: string;
  skillName: string;
};

export type ChatState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  chatLoading: boolean;
  chatMessages: unknown[];
  chatThinkingLevel: string | null;
  chatSending: boolean;
  chatMessage: string;
  chatAttachments: ChatAttachment[];
  chatRunId: string | null;
  chatStream: string | null;
  chatStreamStartedAt: number | null;
  lastError: string | null;
  chatCommands: ChatCommandEntry[];
};

export type ChatEventPayload = {
  runId: string;
  sessionKey: string;
  state: "delta" | "final" | "aborted" | "error";
  message?: unknown;
  errorMessage?: string;
};

export async function loadChatCommands(state: ChatState) {
  if (!state.client || !state.connected) {
    return;
  }
  try {
    const res = await state.client.request<{ commands?: ChatCommandEntry[] }>(
      "commands.list",
      {},
    );
    state.chatCommands = Array.isArray(res.commands) ? res.commands : [];
  } catch {
    // Non-fatal: autocomplete simply won't populate
  }
}

export async function loadChatHistory(state: ChatState) {
  if (!state.client || !state.connected) {
    return;
  }
  state.chatLoading = true;
  state.lastError = null;
  try {
    const res = await state.client.request<{ messages?: Array<unknown>; thinkingLevel?: string }>(
      "chat.history",
      {
        sessionKey: state.sessionKey,
        limit: 200,
      },
    );
    state.chatMessages = Array.isArray(res.messages) ? res.messages : [];
    state.chatThinkingLevel = res.thinkingLevel ?? null;
  } catch (err) {
    state.lastError = String(err);
  } finally {
    state.chatLoading = false;
  }
}

function dataUrlToBase64(dataUrl: string): { content: string; mimeType: string } | null {
  const match = /^data:([^;]+);base64,(.+)$/.exec(dataUrl);
  if (!match) {
    return null;
  }
  return { mimeType: match[1], content: match[2] };
}

/**
 * Derive the HTTP upload URL from the gateway WebSocket URL.
 * ws://host:port -> http://host:port/api/upload
 * wss://host:port -> https://host:port/api/upload
 */
function getUploadUrl(gatewayUrl: string): string {
  return gatewayUrl.replace(/^ws(s?):\/\//, "http$1://").replace(/\/?$/, "/api/upload");
}

/**
 * Upload a single document file to the gateway via HTTP POST.
 * Returns the server-side file path on success, or throws on failure.
 */
async function uploadFileHttp(
  file: File,
  gatewayUrl: string,
  token?: string,
): Promise<{ path: string; fileName: string; size: number; mimeType: string }> {
  const url = getUploadUrl(gatewayUrl);
  const headers: Record<string, string> = {
    "Content-Type": file.type || "application/octet-stream",
    "X-Filename": file.name,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    method: "POST",
    headers,
    body: file,
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: { message: response.statusText } }));
    throw new Error(
      (body as { error?: { message?: string } })?.error?.message ??
        `Upload failed: ${response.status}`,
    );
  }

  const result = (await response.json()) as {
    ok: boolean;
    path: string;
    fileName: string;
    size: number;
    mimeType: string;
  };
  if (!result.ok) {
    throw new Error("Upload response indicated failure");
  }
  return result;
}

export async function sendChatMessage(
  state: ChatState,
  message: string,
  attachments?: ChatAttachment[],
  opts?: { gatewayUrl?: string; token?: string },
): Promise<string | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const msg = message.trim();
  const hasAttachments = attachments && attachments.length > 0;
  if (!msg && !hasAttachments) {
    return null;
  }

  const now = Date.now();

  // Build user message content blocks for local display
  const contentBlocks: Array<{ type: string; text?: string; source?: unknown; fileName?: string }> =
    [];
  if (msg) {
    contentBlocks.push({ type: "text", text: msg });
  }
  if (hasAttachments) {
    for (const att of attachments) {
      if (att.kind === "document") {
        // Show document reference in chat
        contentBlocks.push({
          type: "text",
          text: `[Attached: ${att.fileName ?? "file"}]`,
          fileName: att.fileName,
        });
      } else {
        // Image previews
        contentBlocks.push({
          type: "image",
          source: { type: "base64", media_type: att.mimeType, data: att.dataUrl },
        });
      }
    }
  }

  state.chatMessages = [
    ...state.chatMessages,
    {
      role: "user",
      content: contentBlocks,
      timestamp: now,
    },
  ];

  state.chatSending = true;
  state.lastError = null;
  const runId = generateUUID();
  state.chatRunId = runId;
  state.chatStream = "";
  state.chatStreamStartedAt = now;

  try {
    // Step 1: Upload document attachments via HTTP POST (avoids base64 over WS).
    // Each document gets uploaded as raw binary, and the server returns a file path.
    // Only the path reference is then sent over WebSocket — tiny payload.
    const apiAttachments: Array<{
      type: string;
      mimeType: string;
      fileName?: string;
      content?: string;
      path?: string;
    }> = [];

    if (hasAttachments) {
      for (const att of attachments) {
        if (att.kind === "document" && att.file && opts?.gatewayUrl) {
          // Upload document via HTTP POST
          try {
            const result = await uploadFileHttp(att.file, opts.gatewayUrl, opts.token);
            apiAttachments.push({
              type: "document-ref",
              mimeType: result.mimeType,
              fileName: result.fileName,
              path: result.path,
            });
          } catch (uploadErr) {
            // Fall back to base64 over WS if HTTP upload fails
            console.warn("[chat] HTTP upload failed, falling back to base64:", uploadErr);
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(att.file!);
            });
            const parsed = dataUrlToBase64(dataUrl);
            if (parsed) {
              apiAttachments.push({
                type: "document",
                mimeType: parsed.mimeType,
                fileName: att.fileName ?? "file",
                content: parsed.content,
              });
            }
          }
        } else if (att.kind === "document" && att.dataUrl) {
          // Legacy path: document was already read as dataURL (e.g. pasted)
          const parsed = dataUrlToBase64(att.dataUrl);
          if (parsed) {
            apiAttachments.push({
              type: "document",
              mimeType: parsed.mimeType,
              fileName: att.fileName ?? "file",
              content: parsed.content,
            });
          }
        } else if (att.kind === "image") {
          // Images still go as base64 (small enough for WS)
          const parsed = dataUrlToBase64(att.dataUrl);
          if (parsed) {
            apiAttachments.push({
              type: "image",
              mimeType: parsed.mimeType,
              content: parsed.content,
            });
          }
        }
      }
    }

    // Step 2: Send message + attachment refs over WebSocket
    await state.client.request("chat.send", {
      sessionKey: state.sessionKey,
      message: msg,
      deliver: false,
      idempotencyKey: runId,
      attachments: apiAttachments.length > 0 ? apiAttachments : undefined,
    });
    return runId;
  } catch (err) {
    const error = String(err);
    state.chatRunId = null;
    state.chatStream = null;
    state.chatStreamStartedAt = null;
    state.lastError = error;
    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: "Error: " + error }],
        timestamp: Date.now(),
      },
    ];
    return null;
  } finally {
    state.chatSending = false;
  }
}

export async function abortChatRun(state: ChatState): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  const runId = state.chatRunId;
  try {
    await state.client.request(
      "chat.abort",
      runId ? { sessionKey: state.sessionKey, runId } : { sessionKey: state.sessionKey },
    );
    return true;
  } catch (err) {
    state.lastError = String(err);
    return false;
  }
}

export function handleChatEvent(state: ChatState, payload?: ChatEventPayload) {
  if (!payload) {
    return null;
  }
  if (payload.sessionKey !== state.sessionKey) {
    return null;
  }

  // Final from another run (e.g. sub-agent announce): refresh history to show new message.
  // See https://github.com/igloobot/igloobot/issues/1909
  if (payload.runId && state.chatRunId && payload.runId !== state.chatRunId) {
    if (payload.state === "final") {
      return "final";
    }
    return null;
  }

  if (payload.state === "delta") {
    const next = extractText(payload.message);
    if (typeof next === "string") {
      const current = state.chatStream ?? "";
      if (!current || next.length >= current.length) {
        state.chatStream = next;
      }
    }
  } else if (payload.state === "final") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "aborted") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
  } else if (payload.state === "error") {
    state.chatStream = null;
    state.chatRunId = null;
    state.chatStreamStartedAt = null;
    state.lastError = payload.errorMessage ?? "chat error";
  }
  return payload.state;
}
